import {useState, useEffect, useRef} from 'react'
import {useServices} from '@/services/context'
import {inBrowserRuleConditionError} from '@/lib/cel/ruleengine/cel/RuleConditionValidator'
import type {SodValidateExpression} from '@/types/restriction-rule-request'

const DEBOUNCE_MS = 300

export type ExpressionValidationState = {
    isValidating: boolean
    result: SodValidateExpression | null
    error: Error | null
}

export function useExpressionValidation(conditionExpr: string): ExpressionValidationState {
    const services = useServices()
    const [isValidating, setIsValidating] = useState(false)
    const [result, setResult] = useState<SodValidateExpression | null>(null)
    const [error, setError] = useState<Error | null>(null)
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    // WHY: backend verdicts for successive keystrokes can arrive out of order;
    // tag the latest expression and drop stale responses so a late verdict for
    // an earlier intermediate doesn't clobber the current result (issue 1161).
    const latestExpr = useRef('')

    useEffect(() => {
        if (!conditionExpr.trim()) {
            setResult(null)
            setError(null)
            setIsValidating(false)
            return
        }

        latestExpr.current = conditionExpr

        // WHY: voice the structural "not a boolean condition" rejection
        // instantly, in-browser, without a backend round-trip (issue 1161).
        // Syntax / type / row-reference verdicts still come from the
        // authoritative backend call below.
        const shapeError = inBrowserRuleConditionError(conditionExpr)
        if (shapeError !== null) {
            setResult({valid: false, errors: [{line: 1, column: 0, message: shapeError}]})
            setError(null)
            setIsValidating(false)
            return
        }

        setIsValidating(true)

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
            services.restrictionRuleRequests.validateExpression({conditionExpr})
                .then(res => {
                    if (conditionExpr !== latestExpr.current) return
                    setResult(res)
                    setError(null)
                })
                .catch(err => {
                    if (conditionExpr !== latestExpr.current) return
                    setError(err instanceof Error ? err : new Error(String(err)))
                    setResult(null)
                })
                .finally(() => {
                    if (conditionExpr !== latestExpr.current) return
                    setIsValidating(false)
                })
        }, DEBOUNCE_MS)

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [conditionExpr, services.restrictionRuleRequests])

    return {isValidating, result, error}
}
